// Tool 2: Propojení produktů — seznam skupin (propojení).
// Admin může vytvářet, editovat, mazat skupiny i jejich členy.

import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import { hasModule } from '@/lib/modules';
import type { LinkDisplayType, LinkGroup, LinkGroupMember, MeResponse } from '@sklad-sync/shared';
import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as XLSX from 'xlsx';

type Props = {
  user: MeResponse;
};

const ESHOPS: { code: string; label: string }[] = [
  { code: 'v-mart-cz', label: 'CZ' },
  { code: 'v-mart-sk', label: 'SK' },
];

type Product = {
  shopitem_id: number;
  parent_code: string | null;
  name: string;
  url: string | null;
  image_url: string | null;
  visible: number;
};

// Lehčí typ pro produkty vybrané v modalu (existující nebo nové)
type SelectedProduct = {
  shopitem_id: number;
  parent_code: string | null;
  name: string;
  image_url: string | null;
  member_id: number | null; // null = nově přidaný, číslo = existující v DB
  color_label: string; // jméno barvy (např. "Modrá")
  text_label: string; // textový štítek (např. "SLIM")
  bg_color: string; // hex; prázdný string = nevybráno
  // Sledujeme původní hodnoty (pro detekci změn při uložení existujícího člena)
  initial_color_label?: string;
  initial_text_label?: string;
  initial_bg_color?: string;
};

export function LinkGroupsListPage({ user }: Props) {
  const isAdmin = hasModule(user.modules, 'propojeni');
  const [eshopId, setEshopId] = useState<string>(ESHOPS[0]?.code ?? 'v-mart-cz');
  const [groups, setGroups] = useState<LinkGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [editing, setEditing] = useState<LinkGroup | 'new' | null>(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ groups: LinkGroup[] }>(
        `/api/propojeni/groups?eshop_id=${encodeURIComponent(eshopId)}`,
      );
      setGroups(data.groups);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, [eshopId]);

  useEffect(() => {
    setGroups(null);
    load();
  }, [load]);

  async function testTelegramAlert(kind: 'failure' | 'recovery') {
    try {
      await apiFetch(`/api/propojeni/test-feed-alert?kind=${kind}`, { method: 'POST' });
      setToast({
        msg: `Test alert (${kind}) odeslán do Telegramu`,
        kind: 'success',
      });
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.message : 'Test alertu selhal',
        kind: 'error',
      });
    }
  }

  // Export skupiny do XLSX — kód produktu (párovací klíč mezi e-shopy) + labely.
  // Slouží k přenosu propojení mezi e-shopy: exportuj z CZ, přelož texty
  // v Excelu, naimportuj do SK (viz importGroups níže).
  async function exportGroup(g: LinkGroup) {
    try {
      const data = await apiFetch<{ members: LinkGroupMember[] }>(`/api/propojeni/groups/${g.id}`);
      const rows = data.members.map((m) => ({
        kod: m.product_parent_code ?? '',
        nazev: m.product_name ?? '',
        barva_nazev: m.color_label ?? '',
        barva_hex: m.bg_color ?? '',
        nadpis_barva: g.color_title ?? '',
        text: m.text_label ?? '',
        nadpis_text: g.text_title ?? '',
        poradi: m.display_order,
      }));
      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'propojeni');
      const safeName = g.internal_name.replace(/[^a-z0-9á-ž_-]+/gi, '_').slice(0, 60);
      XLSX.writeFile(book, `propojeni-${safeName}-${g.eshop_id}.xlsx`);
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Export selhal', kind: 'error' });
    }
  }

  // Export VŠECH propojení aktuálního e-shopu do jednoho XLSX — sloupec
  // "propojeni" nese název skupiny, podle něj import řádky zpátky rozdělí.
  async function exportAllGroups() {
    if (!groups || groups.length === 0) {
      setToast({ msg: 'Žádná propojení k exportu', kind: 'error' });
      return;
    }
    try {
      const perGroup = await Promise.all(
        groups.map((g) =>
          apiFetch<{ members: LinkGroupMember[] }>(`/api/propojeni/groups/${g.id}`),
        ),
      );
      const rows = groups.flatMap((g, i) =>
        (perGroup[i]?.members ?? []).map((m) => ({
          propojeni: g.internal_name,
          kod: m.product_parent_code ?? '',
          nazev: m.product_name ?? '',
          barva_nazev: m.color_label ?? '',
          barva_hex: m.bg_color ?? '',
          nadpis_barva: g.color_title ?? '',
          text: m.text_label ?? '',
          nadpis_text: g.text_title ?? '',
          poradi: m.display_order,
        })),
      );
      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'propojeni');
      XLSX.writeFile(book, `propojeni-vsechna-${eshopId}.xlsx`);
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Export selhal', kind: 'error' });
    }
  }

  async function remove(g: LinkGroup) {
    if (!confirm(`Opravdu smazat propojení "${g.internal_name}"? Smaže se i seznam členů.`)) return;
    try {
      await apiFetch(`/api/propojeni/groups/${g.id}`, { method: 'DELETE' });
      setToast({ msg: 'Propojení smazáno', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({
        msg: e instanceof ApiError ? e.message : 'Smazání selhalo',
        kind: 'error',
      });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Propojení produktů</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Skupiny produktů, které se na e-shopu propojí (např. legíny v různých barvách). Zákazník
            na detailu produktu uvidí selektor s ostatními variantami.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => testTelegramAlert('failure')}
              className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-3 py-2 cursor-pointer transition-colors"
              title="Pošle testovací failure alert do Telegram skupiny"
            >
              Test Telegram
            </button>
            <button
              type="button"
              onClick={exportAllGroups}
              className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-3 py-2 cursor-pointer transition-colors"
              title="Exportuje všechna propojení tohoto e-shopu do jednoho XLSX"
            >
              Export vše
            </button>
            <button
              type="button"
              onClick={() => setImporting(true)}
              className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-3 py-2 cursor-pointer transition-colors"
              title="Naimportuje propojení z XLSX exportu (viz tlačítko Export u propojení)"
            >
              Import z XLSX
            </button>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
            >
              Přidat propojení
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-border bg-bg p-1">
        {ESHOPS.map((e) => (
          <button
            key={e.code}
            type="button"
            onClick={() => setEshopId(e.code)}
            className={`text-sm font-semibold rounded-md px-4 py-1.5 transition-colors cursor-pointer ${
              eshopId === e.code
                ? 'bg-surface text-primary shadow-sm'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            {e.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg border-b border-border text-text-secondary">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Název propojení</th>
              <th className="text-left font-semibold px-4 py-3 w-36">Typ zobrazení</th>
              <th className="text-left font-semibold px-4 py-3 w-32">Stav</th>
              <th className="text-right font-semibold px-4 py-3 w-44">Upraveno</th>
              <th className="text-right font-semibold px-4 py-3 w-40">Akce</th>
            </tr>
          </thead>
          <tbody>
            {groups === null && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted py-8">
                  Načítám…
                </td>
              </tr>
            )}
            {groups && groups.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted py-8">
                  Žádná propojení zatím nejsou. Klikni na „Přidat propojení“.
                </td>
              </tr>
            )}
            {groups?.map((g) => {
              const thumbs = g.member_thumbnails ?? [];
              const count = g.member_count ?? 0;
              const VISIBLE_THUMBS = 5;
              return (
                <tr key={g.id} className="border-b border-border last:border-0 hover:bg-bg/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => setEditing(g)}
                          className="text-text font-semibold hover:text-primary cursor-pointer text-left"
                        >
                          {g.internal_name}
                        </button>
                        {g.attribute_label && (
                          <div className="text-xs text-text-muted mt-0.5">{g.attribute_label}</div>
                        )}
                      </div>
                      {thumbs.length > 0 && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {thumbs.slice(0, VISIBLE_THUMBS).map((url, i) => (
                            <img
                              key={i}
                              src={url}
                              alt=""
                              className="w-8 h-8 rounded-md object-contain bg-bg border border-border"
                              loading="lazy"
                            />
                          ))}
                          {count > VISIBLE_THUMBS && (
                            <div className="w-8 h-8 rounded-md bg-bg border border-border flex items-center justify-center text-[11px] font-semibold text-text-secondary">
                              +{count - VISIBLE_THUMBS}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <DisplayTypesBadges types={g.display_types ?? [g.display_type]} />
                  </td>
                  <td className="px-4 py-3">
                    {g.visible ? (
                      <span className="text-xs font-medium text-success">✓ Viditelné</span>
                    ) : (
                      <span className="text-xs font-medium text-text-muted">Skryté</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-text-muted text-xs">
                    {fmtDateTime(g.updated_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => exportGroup(g)}
                          className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-2.5 py-1 cursor-pointer transition-colors"
                          title="Export do XLSX — pro přenos propojení na jiný e-shop"
                        >
                          Export
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(g)}
                          className="text-xs font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded px-2.5 py-1 cursor-pointer transition-colors"
                        >
                          Upravit
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(g)}
                          className="text-xs font-medium text-danger hover:bg-danger-light border border-danger/30 rounded px-2.5 py-1 cursor-pointer transition-colors"
                        >
                          Smazat
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <GroupModal
          group={editing === 'new' ? null : editing}
          eshopId={editing === 'new' ? eshopId : editing.eshop_id}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setToast({ msg, kind: 'success' });
            setEditing(null);
            load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}

      {importing && (
        <ImportModal
          eshopId={eshopId}
          onClose={() => setImporting(false)}
          onDone={(msg) => {
            setToast({ msg, kind: 'success' });
            setImporting(false);
            load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function DisplayTypeBadge({ type }: { type: LinkDisplayType }) {
  if (type === 'barvy') {
    return (
      <span className="text-xs font-semibold text-primary bg-primary-50 px-2 py-0.5 rounded">
        Barvy
      </span>
    );
  }
  if (type === 'text') {
    return (
      <span className="text-xs font-semibold text-text-secondary bg-bg border border-border px-2 py-0.5 rounded">
        Text
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold text-warning bg-warning-light px-2 py-0.5 rounded">
      Obrázky
    </span>
  );
}

function DisplayTypesBadges({ types }: { types: LinkDisplayType[] }) {
  if (!types || types.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <DisplayTypeBadge key={t} type={t} />
      ))}
    </div>
  );
}

// ─── Modal pro vytvoření / editaci ──────────────────────────────────────

type GroupModalProps = {
  group: LinkGroup | null; // null = vytvoření, jinak edit
  eshopId: string; // e-shop, do kterého se skupina zakládá / ve kterém je editovaná
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
};

function GroupModal({ group, eshopId, onClose, onSaved, onError }: GroupModalProps) {
  const isEdit = group !== null;
  const [internalName, setInternalName] = useState(group?.internal_name ?? '');
  const [attributeLabel, setAttributeLabel] = useState(group?.attribute_label ?? '');
  const [colorTitle, setColorTitle] = useState(group?.color_title ?? '');
  const [textTitle, setTextTitle] = useState(group?.text_title ?? '');
  const [displayTypes, setDisplayTypes] = useState<LinkDisplayType[]>(
    group?.display_types ?? (group?.display_type ? [group.display_type] : ['barvy']),
  );
  const [visible, setVisible] = useState<boolean>(group ? !!group.visible : true);
  const [saving, setSaving] = useState(false);

  function toggleDisplayType(t: LinkDisplayType) {
    setDisplayTypes((prev) => {
      const has = prev.includes(t);
      if (has) {
        // Nemůžeme vypnout poslední — musí zůstat alespoň jeden
        if (prev.length === 1) return prev;
        return prev.filter((x) => x !== t);
      }
      return [...prev, t];
    });
  }
  const hasBarvy = displayTypes.includes('barvy');
  const hasText = displayTypes.includes('text');

  // Členové (vybrané produkty)
  const [selected, setSelected] = useState<SelectedProduct[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(isEdit);

  // Drag-and-drop reorder
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  function handleDragStart(idx: number) {
    setDraggingIdx(idx);
  }
  function handleDragOver(idx: number) {
    if (draggingIdx === null || draggingIdx === idx) return;
    setSelected((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(draggingIdx, 1);
      if (moved) arr.splice(idx, 0, moved);
      return arr;
    });
    setDraggingIdx(idx);
  }
  function handleDragEnd() {
    setDraggingIdx(null);
  }

  // Autocomplete
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Při editu načti existující členy
  useEffect(() => {
    if (!group) return;
    (async () => {
      try {
        const data = await apiFetch<{
          group: LinkGroup;
          members: LinkGroupMember[];
        }>(`/api/propojeni/groups/${group.id}`);
        setSelected(
          data.members.map((m) => {
            const name = m.product_name ?? `Produkt #${m.product_shopitem_id}`;
            const colorLabel = m.color_label ?? '';
            const textLabel = m.text_label ?? '';
            const bg = m.bg_color ?? '';
            return {
              shopitem_id: m.product_shopitem_id,
              parent_code: m.product_parent_code,
              name,
              image_url: m.product_image_url,
              member_id: m.id,
              color_label: colorLabel,
              text_label: textLabel,
              bg_color: bg,
              initial_color_label: colorLabel,
              initial_text_label: textLabel,
              initial_bg_color: bg,
            };
          }),
        );
      } catch (e: unknown) {
        onError(e instanceof ApiError ? e.message : 'Chyba načítání členů');
      } finally {
        setLoadingMembers(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id]);

  // Debounce search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ q: query.trim(), limit: '20', eshop_id: eshopId });
        const data = await apiFetch<{ products: Product[] }>(
          `/api/propojeni/products?${params.toString()}`,
        );
        setResults(data.products);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, eshopId]);

  function addProduct(p: Product) {
    if (selected.find((s) => s.shopitem_id === p.shopitem_id)) return; // už je
    setSelected((prev) => [
      ...prev,
      {
        shopitem_id: p.shopitem_id,
        parent_code: p.parent_code,
        name: p.name,
        image_url: p.image_url,
        member_id: null,
        color_label: '',
        text_label: '',
        bg_color: '',
      },
    ]);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  }

  function removeProduct(shopitem_id: number) {
    setSelected((prev) => prev.filter((s) => s.shopitem_id !== shopitem_id));
  }

  function updateMember(shopitem_id: number, patch: Partial<SelectedProduct>) {
    setSelected((prev) =>
      prev.map((s) => (s.shopitem_id === shopitem_id ? { ...s, ...patch } : s)),
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      let groupId: number;
      if (isEdit) {
        await apiFetch(`/api/propojeni/groups/${group.id}`, {
          method: 'PATCH',
          body: {
            internal_name: internalName.trim(),
            attribute_label: attributeLabel.trim(),
            color_title: colorTitle.trim() || null,
            text_title: textTitle.trim() || null,
            display_types: displayTypes,
            visible,
          },
        });
        groupId = group.id;
      } else {
        const created = await apiFetch<{ id: number }>('/api/propojeni/groups', {
          method: 'POST',
          body: {
            internal_name: internalName.trim(),
            attribute_label: attributeLabel.trim(),
            color_title: colorTitle.trim() || null,
            text_title: textTitle.trim() || null,
            display_types: displayTypes,
            visible,
            eshop_id: eshopId,
          },
        });
        groupId = created.id;
      }

      // Při editu — najdi a smaž členy, které tu už nejsou
      if (isEdit) {
        const fresh = await apiFetch<{ members: LinkGroupMember[] }>(
          `/api/propojeni/groups/${groupId}`,
        );
        const keepIds = new Set(
          selected.filter((s) => s.member_id !== null).map((m) => m.member_id),
        );
        for (const m of fresh.members) {
          if (!keepIds.has(m.id)) {
            await apiFetch(`/api/propojeni/groups/${groupId}/members/${m.id}`, {
              method: 'DELETE',
            });
          }
        }
      }

      // Iterate v pořadí selected[] — display_order = index, PATCH/POST podle existence
      for (let i = 0; i < selected.length; i++) {
        const m = selected[i];
        if (!m) continue;
        if (m.member_id !== null) {
          // PATCH existujícího člena (vždy posíláme display_order, aby DnD reorder uložil)
          const patch: {
            color_label?: string | null;
            text_label?: string | null;
            bg_color?: string | null;
            display_order: number;
          } = { display_order: i };
          if (m.color_label !== (m.initial_color_label ?? '')) {
            patch.color_label = m.color_label || null;
          }
          if (m.text_label !== (m.initial_text_label ?? '')) {
            patch.text_label = m.text_label || null;
          }
          if (m.bg_color !== (m.initial_bg_color ?? '')) {
            patch.bg_color = m.bg_color || null;
          }
          await apiFetch(`/api/propojeni/groups/${groupId}/members/${m.member_id}`, {
            method: 'PATCH',
            body: patch,
          });
        } else {
          // POST nového člena s konkrétním display_order
          await apiFetch(`/api/propojeni/groups/${groupId}/members`, {
            method: 'POST',
            body: {
              product_shopitem_id: m.shopitem_id,
              color_label: m.color_label || null,
              text_label: m.text_label || null,
              bg_color: m.bg_color || null,
              display_order: i,
            },
          });
        }
      }

      onSaved(isEdit ? 'Propojení upraveno' : 'Propojení vytvořeno');
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  // Zavřít při Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-xl reveal my-8">
        <form onSubmit={submit} className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-text">
            {isEdit ? 'Upravit propojení' : 'Nové propojení'}{' '}
            <span className="text-xs font-semibold text-text-muted bg-bg border border-border rounded px-1.5 py-0.5 align-middle">
              {ESHOPS.find((e) => e.code === eshopId)?.label ?? eshopId}
            </span>
          </h2>

          {/* 1. Název propojení */}
          <div>
            <label
              className="text-sm font-medium text-text-secondary block mb-1.5"
              htmlFor="internal_name"
            >
              Název propojení <span className="text-danger">*</span>
            </label>
            <input
              id="internal_name"
              type="text"
              value={internalName}
              onChange={(e) => setInternalName(e.target.value)}
              required
              autoFocus
              placeholder="Např. Klasické legíny - barvy"
              className="w-full text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <p className="text-xs text-text-muted mt-1">
              Interní název pro orientaci v adminu — zákazník ho nevidí.
            </p>
          </div>

          {/* 2. Typ zobrazení */}
          <div>
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Typ zobrazení
            </span>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => toggleDisplayType('barvy')}
                className={`text-sm font-medium rounded-lg px-3 py-2.5 border-2 transition-colors cursor-pointer text-left ${
                  hasBarvy
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-border bg-bg text-text-secondary hover:border-text-muted'
                }`}
              >
                <div className="font-semibold">Barvy</div>
                <div className="text-xs text-text-muted mt-0.5">Barevné kruhy</div>
              </button>
              <button
                type="button"
                onClick={() => toggleDisplayType('text')}
                className={`text-sm font-medium rounded-lg px-3 py-2.5 border-2 transition-colors cursor-pointer text-left ${
                  hasText
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-border bg-bg text-text-secondary hover:border-text-muted'
                }`}
              >
                <div className="font-semibold">Text</div>
                <div className="text-xs text-text-muted mt-0.5">Textové štítky</div>
              </button>
            </div>
            <p className="text-xs text-text-muted mt-1">
              Můžeš zapnout oba — selektory se vyrenderují vedle sebe a propojí přes společné členy
              (např. barva + střih).
            </p>
          </div>

          {/* 3. Popis hodnot — per axis */}
          <div className="space-y-3">
            {hasBarvy && (
              <div>
                <label
                  className="text-sm font-medium text-text-secondary block mb-1.5"
                  htmlFor="color_title"
                >
                  Popis hodnot pro Barvy (nepovinné)
                </label>
                <input
                  id="color_title"
                  type="text"
                  value={colorTitle}
                  onChange={(e) => setColorTitle(e.target.value)}
                  placeholder="Např. Barva"
                  className="w-full text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <p className="text-xs text-text-muted mt-1">
                  Zobrazí se nad selektorem barev na frontě e-shopu.
                </p>
              </div>
            )}
            {hasText && (
              <div>
                <label
                  className="text-sm font-medium text-text-secondary block mb-1.5"
                  htmlFor="text_title"
                >
                  Popis hodnot pro Text (nepovinné)
                </label>
                <input
                  id="text_title"
                  type="text"
                  value={textTitle}
                  onChange={(e) => setTextTitle(e.target.value)}
                  placeholder="Např. Střih"
                  className="w-full text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <p className="text-xs text-text-muted mt-1">
                  Zobrazí se nad textovým selektorem na frontě e-shopu.
                </p>
              </div>
            )}
          </div>

          {/* 4. Produkty */}
          <div>
            <span className="text-sm font-medium text-text-secondary block mb-1.5">
              Produkty v propojení
            </span>

            {/* Autocomplete vyhledávač */}
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Hledat produkt podle kódu nebo názvu…"
                className="w-full text-sm text-text placeholder:text-text-muted bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
              />
              {showDropdown && query.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-card-hover max-h-72 overflow-y-auto z-10">
                  {searching && <div className="px-3 py-2 text-sm text-text-muted">Hledám…</div>}
                  {!searching && results.length === 0 && (
                    <div className="px-3 py-2 text-sm text-text-muted">Žádné výsledky.</div>
                  )}
                  {!searching &&
                    results.map((p) => {
                      const already = selected.find((s) => s.shopitem_id === p.shopitem_id);
                      return (
                        <button
                          key={p.shopitem_id}
                          type="button"
                          onClick={() => !already && addProduct(p)}
                          disabled={!!already}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {p.image_url ? (
                            <img
                              src={p.image_url}
                              alt=""
                              className="w-8 h-8 rounded object-cover bg-bg flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-bg border border-border flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-text truncate">{p.name}</div>
                            <div className="text-xs text-text-muted font-mono">
                              {p.parent_code ?? '—'} · #{p.shopitem_id}
                            </div>
                          </div>
                          {already && <span className="text-xs text-text-muted">už přidán</span>}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
            {loadingMembers && <div className="text-xs text-text-muted mt-2">Načítám členy…</div>}
            {!loadingMembers && selected.length > 0 && (
              <div className="mt-2 space-y-2">
                <div className="text-xs text-text-muted">
                  Vybráno: <strong className="text-text">{selected.length}</strong>
                </div>
                {selected.map((p, idx) => (
                  <MemberCard
                    key={p.shopitem_id}
                    index={idx}
                    member={p}
                    displayTypes={displayTypes}
                    isDragging={draggingIdx === idx}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onChange={(patch) => updateMember(p.shopitem_id, patch)}
                    onRemove={() => removeProduct(p.shopitem_id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 5. Viditelnost */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={visible}
                onChange={(e) => setVisible(e.target.checked)}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              />
              <span className="text-sm font-medium text-text">Viditelné</span>
            </label>
            <p className="text-xs text-text-muted mt-1 ml-6">
              Pokud vypneš, selektor se na e-shopu nezobrazí.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-4 py-2 cursor-pointer transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving || !internalName.trim()}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-5 py-2 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : isEdit ? 'Uložit' : 'Vytvořit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Karta jednoho člena (Popis + Barva pozadí) ─────────────────────────

type MemberCardProps = {
  member: SelectedProduct;
  index: number;
  displayTypes: LinkDisplayType[];
  isDragging: boolean;
  onDragStart: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDragEnd: () => void;
  onChange: (patch: Partial<SelectedProduct>) => void;
  onRemove: () => void;
};

function MemberCard({
  member,
  index,
  displayTypes,
  isDragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onChange,
  onRemove,
}: MemberCardProps) {
  const showBarvy = displayTypes.includes('barvy');
  const showText = displayTypes.includes('text');
  const cardRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={cardRef}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDragEnd={onDragEnd}
      onDrop={(e) => {
        e.preventDefault();
        onDragEnd();
      }}
      className={`bg-bg border border-border rounded-lg p-3 transition-opacity ${
        isDragging ? 'opacity-40 ring-2 ring-primary' : ''
      }`}
    >
      {/* Header: drag handle + foto + název + kód + odebrat */}
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          draggable
          onDragStart={(e) => {
            if (cardRef.current) {
              e.dataTransfer.setDragImage(cardRef.current, 0, 0);
            }
            e.dataTransfer.effectAllowed = 'move';
            // Drag handle nesmí spustit submit formuláře / žádné side effect
            e.stopPropagation();
            onDragStart(index);
          }}
          className="text-text-muted hover:text-text px-1.5 py-2 cursor-grab active:cursor-grabbing select-none leading-none"
          aria-label="Přesunout pořadí"
          title="Přesunout"
        >
          ⋮⋮
        </button>
        {member.image_url ? (
          <img
            src={member.image_url}
            alt=""
            className="w-9 h-9 rounded object-cover bg-surface flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="w-9 h-9 rounded bg-surface border border-border flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-text truncate">{member.name}</div>
          <div className="text-[11px] text-text-muted font-mono">
            {member.parent_code ?? '—'} · #{member.shopitem_id}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-text-muted hover:text-danger text-xl leading-none px-2 cursor-pointer"
          aria-label="Odebrat produkt"
        >
          ×
        </button>
      </div>

      <div className="space-y-2">
        {showBarvy && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-text-secondary block mb-1">
                Název barvy
              </label>
              <input
                type="text"
                value={member.color_label}
                onChange={(e) => onChange({ color_label: e.target.value })}
                placeholder="Černá"
                className="w-full text-sm text-text placeholder:text-text-muted bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-text-secondary block mb-1">
                Kód barvy
              </label>
              <input
                type="text"
                value={member.bg_color}
                onChange={(e) => onChange({ bg_color: e.target.value })}
                placeholder="#000000"
                className="w-full text-sm text-text placeholder:text-text-muted bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all font-mono"
              />
            </div>
          </div>
        )}
        {showText && (
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">Text</label>
            <input
              type="text"
              value={member.text_label}
              onChange={(e) => onChange({ text_label: e.target.value })}
              placeholder="Dlouhý rukáv"
              className="w-full text-sm text-text placeholder:text-text-muted bg-surface border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Import propojení z XLSX (viz Export u existující skupiny) ─────────

type ImportRow = {
  propojeni: string; // prázdné = soubor exportoval jen jednu skupinu (starší formát)
  kod: string;
  nazev: string;
  barva_nazev: string;
  barva_hex: string;
  nadpis_barva: string; // popis nad selektorem barev (color_title na skupině)
  text: string;
  nadpis_text: string; // popis nad textovým selektorem (text_title na skupině)
  poradi: number;
};

type ImportModalProps = {
  eshopId: string; // e-shop, do kterého se importuje
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
};

function ImportModal({ eshopId, onClose, onDone, onError }: ImportModalProps) {
  const [internalName, setInternalName] = useState('');
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [resolved, setResolved] = useState<Map<string, Product> | null>(null);
  const [checking, setChecking] = useState(false);
  const [creating, setCreating] = useState(false);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Z "propojeni-Nazev_propojeni-v-mart-cz.xlsx" → "Nazev propojeni" (odstraní
    // náš prefix/suffix z exportu, podtržítka na mezery). Použije se jen jako
    // fallback jméno, když soubor nemá sloupec "propojeni" (starší export
    // jedné skupiny) — u vícegroupového exportu se jméno bere z toho sloupce.
    const guess = file.name
      .replace(/\.xlsx?$/i, '')
      .replace(/^propojeni-(vsechna-)?/, '')
      .replace(/-v-mart-[a-z]{2}$/i, '')
      .replace(/_/g, ' ')
      .trim();
    setInternalName(guess);
    setRows(null);
    setResolved(null);

    let parsed: ImportRow[];
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) throw new Error('Soubor neobsahuje žádný list');
      const sheet = wb.Sheets[sheetName];
      if (!sheet) throw new Error('List je prázdný');
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      parsed = json
        .map((r, i) => ({
          propojeni: String(r.propojeni ?? '').trim(),
          kod: String(r.kod ?? '').trim(),
          nazev: String(r.nazev ?? '').trim(),
          barva_nazev: String(r.barva_nazev ?? '').trim(),
          barva_hex: String(r.barva_hex ?? '').trim(),
          nadpis_barva: String(r.nadpis_barva ?? '').trim(),
          text: String(r.text ?? '').trim(),
          nadpis_text: String(r.nadpis_text ?? '').trim(),
          poradi: Number(r.poradi ?? i) || i,
        }))
        .filter((r) => r.kod);
    } catch {
      onError('Soubor se nepodařilo přečíst — musí to být XLSX export z tohoto nástroje.');
      return;
    }
    if (parsed.length === 0) {
      onError('V souboru nejsou žádné řádky s kódem produktu.');
      return;
    }
    setRows(parsed);

    setChecking(true);
    try {
      const codes = [...new Set(parsed.map((r) => r.kod))];
      const params = new URLSearchParams({ eshop_id: eshopId, codes: codes.join(',') });
      const data = await apiFetch<{ products: Product[] }>(
        `/api/propojeni/products?${params.toString()}`,
      );
      const map = new Map<string, Product>();
      for (const p of data.products) {
        if (p.parent_code && !map.has(p.parent_code)) map.set(p.parent_code, p);
      }
      setResolved(map);
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.message : 'Ověření kódů selhalo');
    } finally {
      setChecking(false);
    }
  }

  // Víc skupin v jednom souboru pozná podle sloupce "propojeni" — pokud ho
  // aspoň jeden řádek má vyplněný, řádky se podle něj rozdělí do víc skupin.
  // Jinak (starší export jedné skupiny) jde vše do jedné, pojmenované z pole nahoře.
  const hasGroupColumn = rows?.some((r) => r.propojeni) ?? false;
  const groupedRows = useMemo(() => {
    if (!rows) return [] as { name: string; rows: ImportRow[] }[];
    const order: string[] = [];
    const map = new Map<string, ImportRow[]>();
    for (const r of rows) {
      const key = hasGroupColumn
        ? r.propojeni || '(bez názvu)'
        : internalName.trim() || '(bez názvu)';
      if (!map.has(key)) {
        order.push(key);
        map.set(key, []);
      }
      map.get(key)?.push(r);
    }
    return order.map((name) => ({ name, rows: map.get(name) ?? [] }));
  }, [rows, hasGroupColumn, internalName]);

  const matchedRows = rows && resolved ? rows.filter((r) => resolved.has(r.kod)) : [];
  const unmatchedRows = rows && resolved ? rows.filter((r) => !resolved.has(r.kod)) : [];

  async function create() {
    if (!resolved || matchedRows.length === 0) return;
    setCreating(true);
    try {
      let createdGroups = 0;
      let createdMembers = 0;
      for (const g of groupedRows) {
        const matched = g.rows.filter((r) => resolved.has(r.kod));
        if (matched.length === 0) continue; // celá skupina bez napárovaného produktu

        const displayTypes: LinkDisplayType[] = [];
        if (matched.some((r) => r.barva_nazev || r.barva_hex)) displayTypes.push('barvy');
        if (matched.some((r) => r.text)) displayTypes.push('text');
        if (displayTypes.length === 0) displayTypes.push('barvy');

        const name =
          g.name !== '(bez názvu)' ? g.name : `Import ${new Date().toLocaleDateString('cs')}`;
        // Popis nad selektory je vlastnost skupiny, na řádcích je jen kvůli plochému
        // formátu XLSX — vezmeme první neprázdnou hodnotu napříč jejími řádky.
        const colorTitle = g.rows.find((r) => r.nadpis_barva)?.nadpis_barva ?? null;
        const textTitle = g.rows.find((r) => r.nadpis_text)?.nadpis_text ?? null;

        const created = await apiFetch<{ id: number }>('/api/propojeni/groups', {
          method: 'POST',
          body: {
            internal_name: name,
            attribute_label: '',
            color_title: colorTitle,
            text_title: textTitle,
            display_types: displayTypes,
            visible: true,
            eshop_id: eshopId,
          },
        });
        createdGroups++;

        for (const r of matched) {
          const p = resolved.get(r.kod);
          if (!p) continue;
          await apiFetch(`/api/propojeni/groups/${created.id}/members`, {
            method: 'POST',
            body: {
              product_shopitem_id: p.shopitem_id,
              color_label: r.barva_nazev || null,
              text_label: r.text || null,
              bg_color: r.barva_hex || null,
              display_order: r.poradi,
            },
          });
          createdMembers++;
        }
      }

      const suffix =
        unmatchedRows.length > 0 ? `, ${unmatchedRows.length} produktů nenalezeno` : '';
      onDone(
        createdGroups > 1
          ? `Vytvořeno ${createdGroups} propojení, ${createdMembers} produktů${suffix}`
          : `Propojení vytvořeno — napárováno ${createdMembers}/${rows?.length ?? 0} produktů${suffix}`,
      );
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Import selhal');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-xl reveal my-8 p-6 space-y-4">
        <h2 className="text-lg font-bold text-text">
          Import propojení z XLSX{' '}
          <span className="text-xs font-semibold text-text-muted bg-bg border border-border rounded px-1.5 py-0.5 align-middle">
            {ESHOPS.find((e) => e.code === eshopId)?.label ?? eshopId}
          </span>
        </h2>
        <p className="text-xs text-text-muted">
          Vezme export z jiného e-shopu (tlačítko „Export“ u propojení), spáruje produkty podle kódu
          a založí nové propojení v aktuálně vybraném e-shopu ({eshopId}).
        </p>

        {!hasGroupColumn && (
          <div>
            <label
              className="text-sm font-medium text-text-secondary block mb-1.5"
              htmlFor="import_name"
            >
              Název propojení
            </label>
            <input
              id="import_name"
              type="text"
              value={internalName}
              onChange={(e) => setInternalName(e.target.value)}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <p className="text-xs text-text-muted mt-1">
              Soubor obsahuje jedno propojení bez sloupce „propojeni“ — jméno se použije pro něj.
            </p>
          </div>
        )}

        <div>
          <label
            className="text-sm font-medium text-text-secondary block mb-1.5"
            htmlFor="import_file"
          >
            XLSX soubor <span className="text-danger">*</span>
          </label>
          <input
            id="import_file"
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFile}
            className="w-full text-sm text-text file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-bg file:text-text-secondary file:cursor-pointer cursor-pointer"
          />
        </div>

        {checking && <p className="text-sm text-text-muted">Ověřuji kódy produktů…</p>}

        {rows && resolved && !checking && (
          <div className="text-sm space-y-2">
            <p className="text-text">
              Napárováno <strong className="text-success">{matchedRows.length}</strong> /{' '}
              {rows.length} produktů
              {hasGroupColumn && (
                <>
                  {' '}
                  ve <strong className="text-text">{groupedRows.length}</strong> propojeních
                </>
              )}
              {unmatchedRows.length > 0 && (
                <>
                  {' '}
                  — <strong className="text-danger">{unmatchedRows.length}</strong> se nenašlo (asi
                  ještě nejsou nasyncované, nebo mají jiný kód).
                </>
              )}
            </p>

            {hasGroupColumn && (
              <div className="max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {groupedRows.map((g) => {
                  const matched = g.rows.filter((r) => resolved.has(r.kod)).length;
                  return (
                    <div
                      key={g.name}
                      className="flex items-center justify-between px-3 py-1.5 bg-bg"
                    >
                      <span className="text-text truncate">{g.name}</span>
                      <span
                        className={`text-xs font-medium flex-shrink-0 ml-2 ${matched === 0 ? 'text-danger' : 'text-text-muted'}`}
                      >
                        {matched}/{g.rows.length}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {unmatchedRows.length > 0 && (
              <div className="max-h-32 overflow-y-auto bg-bg border border-border rounded-lg px-3 py-2">
                <div className="text-xs text-text-muted font-mono">
                  {unmatchedRows.map((r) => r.kod).join(', ')}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-4 py-2 cursor-pointer transition-colors"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={create}
            disabled={creating || checking || matchedRows.length === 0}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-5 py-2 transition-colors cursor-pointer disabled:opacity-50"
          >
            {creating
              ? 'Vytvářím…'
              : hasGroupColumn
                ? `Vytvořit ${groupedRows.filter((g) => g.rows.some((r) => resolved?.has(r.kod))).length} propojení`
                : `Vytvořit propojení (${matchedRows.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
